import { Typography } from '@material-ui/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ManagementTimelineMarker,
  ManagementTimelineQuery,
} from '@internal/spassu-code-contract';
import type {
  CockpitArtifactContentState,
  CockpitTimelineState,
} from '../controller/cockpitControllerTypes';
import type {
  CockpitNodeKind,
  CockpitViewModel,
} from '../model/cockpitViewModel';
import { allCockpitNodes } from '../model/cockpitViewModel';
import type { CockpitSelectionState } from '../selection/cockpitSelection';
import { ArtifactsSection } from './ArtifactsSection';
import { DeliverySection } from './DeliverySection';
import { MetricsBarSection } from './MetricsBarSection';
import { PhaseStepperSection } from './PhaseStepperSection';
import { TimelineSection } from './TimelineSection';
import { useCockpitStyles } from './cockpitStyles';

type SelectionController = CockpitSelectionState & {
  select(kind: CockpitNodeKind, id: string): void;
  clear(): void;
};

export interface CockpitShellProps {
  model: CockpitViewModel;
  metricsPreferenceScope?: string | null;
  selection: SelectionController;
  artifactContent?: Record<string, CockpitArtifactContentState>;
  loadArtifactContent?: (artifactId: string) => Promise<string>;
  loadMoreArtifactContent?: (artifactId: string) => Promise<string>;
  timeline: CockpitTimelineState;
  setTimelineQuery(query: ManagementTimelineQuery): void;
  loadMoreTimeline(): void;
}

/**
 * The selection reveals itself inside the node that represents it, so the shell
 * only announces it and explains the case where the node is not in this
 * revision — there is no panel left to say it.
 */
const SelectionAnnouncement = ({
  className,
  model,
  selection,
  timelineMarkers,
}: {
  className: string;
  model: CockpitViewModel;
  selection: CockpitSelectionState['selected'];
  timelineMarkers?: ManagementTimelineMarker[];
}) => (
  <>
    {selection && (
      <Typography variant="caption">
        {selectionCaption(model, selection, timelineMarkers)}
      </Typography>
    )}
    <div className={className} aria-live="polite">
      {selection
        ? `Selecionado: ${selection.id}. ${selectionCaption(
            model,
            selection,
            timelineMarkers,
          )}`
        : 'Seleção limpa'}
    </div>
  </>
);

function selectionCaption(
  model: CockpitViewModel,
  selection: NonNullable<CockpitSelectionState['selected']>,
  timelineMarkers?: ManagementTimelineMarker[],
): string {
  const revision = `Seleção da revisão ${selection.revision ?? 'viva'}.`;
  return isSelectionResolvable(model, selection, timelineMarkers)
    ? revision
    : `${revision} O nó relacionado não está disponível nesta revisão.`;
}

function isSelectionResolvable(
  model: CockpitViewModel,
  selection: NonNullable<CockpitSelectionState['selected']>,
  timelineMarkers?: ManagementTimelineMarker[],
): boolean {
  if (selection.kind === 'timeline') {
    return Boolean(timelineMarkers?.some(marker => marker.id === selection.id));
  }
  return allCockpitNodes(model).some(
    node => node.id === selection.id && node.kind === selection.kind,
  );
}

/**
 * A rail segment stands for a skill, but the page no longer shows skills as
 * nodes (RN-COCK-03): selecting the segment opens the artifact that skill
 * produced. A skill without an artifact is still selected so the live region
 * can say so, and an open artifact highlights the segment of its producer.
 */
function artifactOfSkill(
  model: CockpitViewModel,
  skillId: string,
): string | null {
  const skill = model.skills.find(item => item.id === skillId);
  const artifactId = skill?.artifactIds.find(id =>
    model.artifacts.some(artifact => artifact.id === id),
  );
  return artifactId ?? null;
}

function railSelectedId(
  model: CockpitViewModel,
  selected: CockpitSelectionState['selected'],
): string | null {
  if (!selected) return null;
  if (selected.kind === 'skill') return selected.id;
  if (selected.kind !== 'artifact') return null;
  const producer = model.skills.find(skill =>
    skill.artifactIds.includes(selected.id),
  );
  return producer?.id ?? null;
}

type CockpitSectionsProps = Omit<CockpitShellProps, 'selection'> & {
  selection: CockpitSelectionState;
  select: SelectionController['select'];
  clear: SelectionController['clear'];
};

/**
 * The stepper filters the artifacts list to one phase (RN-COCK-09). The filter
 * is a page state, not a selection: it survives opening and closing cards, and
 * steps aside when a selection (from the rail, say) points at an artifact of
 * another phase, so the open card is never hidden by the filter.
 */
function usePhaseFilter(
  model: CockpitViewModel,
  selection: CockpitSelectionState,
  select: SelectionController['select'],
  clear: SelectionController['clear'],
) {
  const [filterKey, setFilterKey] = useState<string | null>(null);
  const selectedArtifactId =
    selection.selected?.kind === 'artifact' ? selection.selected.id : null;
  const filterStep = model.phases?.find(step => step.key === filterKey) ?? null;
  const effectiveStep =
    filterStep &&
    (selectedArtifactId === null ||
      filterStep.artifactIds.includes(selectedArtifactId))
      ? filterStep
      : null;
  const artifacts = effectiveStep
    ? model.artifacts.filter(artifact =>
        effectiveStep.artifactIds.includes(artifact.id),
      )
    : model.artifacts;
  const selectPhase = (key: string) => {
    if (key === filterKey) {
      setFilterKey(null);
      return;
    }
    setFilterKey(key);
    const step = model.phases?.find(item => item.key === key);
    if (!step) return;
    if (step.artifactId && step.artifactId !== selectedArtifactId) {
      select('artifact', step.artifactId);
    } else if (!step.artifactId && selectedArtifactId !== null) {
      clear();
    }
  };
  return {
    filterKey: effectiveStep?.key ?? null,
    filterLabel: effectiveStep?.label ?? null,
    artifacts,
    selectPhase,
    clearFilter: () => setFilterKey(null),
  };
}

const CockpitSections = (props: CockpitSectionsProps) => {
  const { model, selection, select, clear } = props;
  const classes = useCockpitStyles();
  const selected = selection.selected;
  const phaseFilter = usePhaseFilter(model, selection, select, clear);
  const selectedIdOf = (kind: CockpitNodeKind) =>
    selected?.kind === kind ? selected.id : null;
  return (
    <div className={classes.shell}>
      <PhaseStepperSection
        phases={model.phases}
        selectedKey={phaseFilter.filterKey}
        onSelectPhase={phaseFilter.selectPhase}
      />
      <ArtifactsSection
        artifacts={phaseFilter.artifacts}
        sectionState={model.sectionState.artifacts}
        selectedId={selectedIdOf('artifact')}
        onSelect={id => select('artifact', id)}
        filterLabel={phaseFilter.filterLabel}
        onClearFilter={phaseFilter.clearFilter}
        artifactContent={props.artifactContent}
        loadArtifactContent={props.loadArtifactContent}
        loadMoreArtifactContent={props.loadMoreArtifactContent}
      />
      <MetricsBarSection
        metrics={model.metrics}
        rail={model.rail}
        preferenceScope={props.metricsPreferenceScope}
        selectedRailId={railSelectedId(model, selected)}
        onSelectRail={skillId => {
          const artifactId = artifactOfSkill(model, skillId);
          if (artifactId) select('artifact', artifactId);
          else select('skill', skillId);
        }}
      />
      <DeliverySection delivery={model.delivery} />
      <TimelineSection
        timeline={props.timeline}
        model={model}
        selectedId={selectedIdOf('timeline')}
        onSelect={id => select('timeline', id)}
        onQueryChange={props.setTimelineQuery}
        onLoadMore={props.loadMoreTimeline}
      />
    </div>
  );
};

export const CockpitShell = (props: CockpitShellProps) => {
  const { model, selection } = props;
  const classes = useCockpitStyles();
  const focusSelection = useFocusRestoringSelection(selection);
  // no selection has a panel of its own any more, so the shell owns Escape
  useEscapeToClear(Boolean(selection.selected), focusSelection.clear);
  return (
    <main className={classes.shellLayout}>
      <CockpitSections
        {...props}
        select={focusSelection.select}
        clear={focusSelection.clear}
      />
      <SelectionAnnouncement
        className={classes.live}
        model={model}
        selection={selection.selected}
        timelineMarkers={props.timeline.page?.markers}
      />
    </main>
  );
};

function useFocusRestoringSelection(selection: SelectionController) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const select = useCallback(
    (kind: CockpitNodeKind, id: string) => {
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      selection.select(kind, id);
    },
    [selection],
  );
  const clear = useCallback(() => {
    selection.clear();
    const trigger = triggerRef.current;
    window.setTimeout(() => trigger?.focus(), 0);
  }, [selection]);
  return { select, clear };
}

function useEscapeToClear(active: boolean, clear: () => void): void {
  useEffect(() => {
    if (!active) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clear();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [active, clear]);
}
